/* Shoe Me wishlist (replaces Swym Wishlist Plus).
 * - Heart on every product card, a "Save to wishlist" button on product pages, and a heart with a count in the header.
 * - Saved in the shopper's browser (localStorage). If a logged-in customer has an imported list in the customer
 *   metafield shoe_me.wishlist, the wishlist page merges it in once.
 * - The wishlist page (/pages/wishlist, section shoe-me-wishlist) renders the saved products and a share link.
 * - Links from the wishlist carry utm_source=shoeme-wishlist so orders from it show up in Shopify analytics.
 */
(() => {
  'use strict';
  if (window.ShoeMeWishlist) return;

  const KEY = 'shoeme-wishlist-v1';
  const PAGE_URL = '/pages/wishlist';
  const UTM = 'utm_source=shoeme-wishlist&utm_medium=wishlist';

  const HEART = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="M12 21s-7.5-4.6-10-9.3C.4 8.4 2.3 4.5 6 4.1c2.2-.2 3.8 1 6 3.2 2.2-2.2 3.8-3.4 6-3.2 3.7.4 5.6 4.3 4 7.6C19.5 16.4 12 21 12 21z" fill="currentColor"/></svg>';

  /* ---------- storage ---------- */
  const read = () => {
    try {
      const list = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(list) ? list.filter(item => item && item.h) : [];
    } catch (error) {
      return [];
    }
  };
  const write = list => {
    try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (error) { /* private mode etc. */ }
    document.dispatchEvent(new CustomEvent('shoeme:wishlist-change', { detail: { list } }));
  };
  const has = handle => read().some(item => item.h === handle);
  const add = (handle, variantId) => {
    if (!handle || has(handle)) return;
    write([{ h: handle, v: variantId || null, t: Date.now() }, ...read()]);
  };
  const remove = handle => write(read().filter(item => item.h !== handle));
  const toggle = (handle, variantId) => {
    if (has(handle)) {
      remove(handle);
      return false;
    }
    add(handle, variantId);
    return true;
  };
  const merge = handles => {
    const list = read();
    const known = new Set(list.map(item => item.h));
    handles.filter(h => h && !known.has(h)).forEach(h => list.push({ h, v: null, t: Date.now() }));
    write(list);
  };

  const handleFromUrl = url => {
    const match = String(url || '').match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  };

  /* ---------- styles ---------- */
  const css = `
    .smw-heart { position: absolute; top: 8px; right: 8px; z-index: 3; width: 36px; height: 36px; border-radius: 50%;
      border: 0; background: rgba(255,255,255,.9); color: #9a9a9a; display: inline-flex; align-items: center;
      justify-content: center; cursor: pointer; box-shadow: 0 1px 4px rgba(0,0,0,.15); transition: transform .15s, color .15s; }
    .smw-heart:hover { transform: scale(1.08); color: #ed3093; }
    .smw-heart[aria-pressed="true"] { color: #ed3093; }
    .productitem__image-container { position: relative; }
    .smw-save { display: inline-flex; align-items: center; gap: 8px; margin-top: 12px; padding: 10px 14px; border: 1px solid #ddd;
      border-radius: 6px; background: #fff; color: #1d1d1d; font-size: 14px; cursor: pointer; }
    .smw-save svg { color: #9a9a9a; }
    .smw-save[aria-pressed="true"] svg { color: #ed3093; }
    .smw-header-link { position: relative; display: inline-flex; align-items: center; align-self: center; color: inherit; padding: 0 10px 0 6px; }
    .site-header-cart:has(.smw-header-link) { left: auto !important; width: auto !important; display: flex; align-items: center; }
    .site-header-right:has(.smw-header-link) .site-header-actions__account-link { margin-right: 96px !important; }
    .smw-header-link svg { width: 24px; height: 24px; color: inherit; fill: none; }
    .smw-header-link svg path { fill: none; stroke: currentColor; stroke-width: 2; }
    .smw-count { position: absolute; top: -6px; right: -4px; min-width: 18px; height: 18px; padding: 0 5px; border-radius: 9px;
      background: #ed3093; color: #fff; font-size: 11px; line-height: 18px; text-align: center; }
    .smw-count:empty { display: none; }
    .smw-toast { position: fixed; left: 50%; bottom: 24px; transform: translateX(-50%) translateY(20px); opacity: 0; z-index: 10001;
      background: #1d1d1d; color: #fff; padding: 10px 16px; border-radius: 6px; font-size: 14px; transition: all .25s; pointer-events: none; }
    .smw-toast.is-visible { opacity: 1; transform: translateX(-50%) translateY(0); pointer-events: auto; }
    .smw-toast a { color: #fff; text-decoration: underline; margin-left: 6px; }
  `;
  const style = document.createElement('style');
  style.textContent = css;
  document.head.appendChild(style);

  /* ---------- toast ---------- */
  let toastTimer = null;
  const toast = html => {
    let el = document.querySelector('.smw-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'smw-toast';
      el.setAttribute('role', 'status');
      document.body.appendChild(el);
    }
    el.innerHTML = html;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 3000);
  };

  /* ---------- hearts on product cards ---------- */
  const decorateCards = (root = document) => {
    root.querySelectorAll('.productgrid--item[data-product-quickshop-url]').forEach(card => {
      const container = card.querySelector('.productitem__image-container');
      if (!container || container.querySelector('.smw-heart')) return;
      const handle = handleFromUrl(card.getAttribute('data-product-quickshop-url'));
      if (!handle) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'smw-heart';
      button.dataset.smwHandle = handle;
      button.innerHTML = HEART;
      container.appendChild(button);
    });
    syncButtons();
  };

  /* ---------- product page button ---------- */
  const decorateProductPage = () => {
    if (!location.pathname.includes('/products/')) return;
    const handle = handleFromUrl(location.pathname);
    if (!handle) return;
    const anchor = document.querySelector('.product-form__action-wrapper') || document.querySelector('.product-title');
    if (!anchor || document.querySelector('.smw-save')) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'smw-save';
    button.dataset.smwHandle = handle;
    button.dataset.smwLabel = 'page';
    button.innerHTML = `${HEART}<span></span>`;
    anchor.insertAdjacentElement('afterend', button);
    syncButtons();
  };

  /* ---------- header link ---------- */
  const decorateHeader = () => {
    // Sits inside the cart icon's container (shown on desktop and mobile), just before the cart.
    const cart = document.querySelector('.site-header-cart');
    if (!cart || document.querySelector('.smw-header-link')) return;
    const link = document.createElement('a');
    link.className = 'smw-header-link';
    link.href = PAGE_URL;
    link.setAttribute('aria-label', 'Wishlist');
    link.innerHTML = `${HEART}<span class="smw-count"></span>`;
    cart.insertBefore(link, cart.firstElementChild);
    syncCount();
  };

  const syncCount = () => {
    const count = read().length;
    document.querySelectorAll('.smw-count').forEach(el => { el.textContent = count ? String(count) : ''; });
    document.querySelectorAll('.smw-header-link').forEach(a => a.setAttribute('aria-label', `Wishlist (${count})`));
  };

  const syncButtons = () => {
    const saved = new Set(read().map(item => item.h));
    document.querySelectorAll('[data-smw-handle]').forEach(button => {
      const on = saved.has(button.dataset.smwHandle);
      button.setAttribute('aria-pressed', on ? 'true' : 'false');
      button.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Save to wishlist');
      if (button.dataset.smwLabel === 'page') {
        button.querySelector('span').textContent = on ? 'Saved to your wishlist' : 'Save to wishlist';
      }
    });
  };

  document.addEventListener('click', event => {
    const button = event.target.closest('[data-smw-handle]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    const variant = new URLSearchParams(location.search).get('variant');
    const added = toggle(button.dataset.smwHandle, button.dataset.smwLabel === 'page' ? variant : null);
    toast(added ? `Saved to your wishlist <a href="${PAGE_URL}">View</a>` : 'Removed from your wishlist');
  }, true);

  document.addEventListener('shoeme:wishlist-change', () => { syncButtons(); syncCount(); });
  window.addEventListener('storage', event => { if (event.key === KEY) { syncButtons(); syncCount(); } });

  // Cards added later (filters, infinite scroll, quick search) get hearts too.
  let pending = false;
  const observer = new MutationObserver(mutations => {
    if (pending || !mutations.some(m => [...m.addedNodes].some(n => n.nodeType === 1))) return;
    pending = true;
    requestAnimationFrame(() => { pending = false; decorateCards(); decorateProductPage(); });
  });

  const init = () => {
    decorateHeader();
    decorateCards();
    decorateProductPage();
    observer.observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  window.ShoeMeWishlist = { read, add, remove, toggle, merge, has, PAGE_URL, UTM, HEART, toast };
})();
